import { TrashIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import { IBillableDevice } from "../../../@types/admin/AdminBillableDevice";
import { TableSorted } from "../../../components/TableSorted";
import { useArchiveBillableDevice } from "../../../hooks/mutations/admin/billableDevices";
import { useBillableDevices } from "../../../hooks/queries/admin/billableDevices";
import { useModal } from "../../../hooks/useModal";
import { useToast } from "../../../hooks/useToast";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  ErrorMessage,
  FilterButton,
  FiltersActions,
  FiltersContainer,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  PageContainer,
  PageHeader,
  PageTitle,
  RowAction,
  SearchInput,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";
import { AddBillableDeviceModal } from "./AddBillableDeviceModal";
import { ModalBodyText } from "./AdminBillableDevices.styles";

interface IArchiveBillableDeviceModalProps {
  device: IBillableDevice;
  onClose: () => void;
}

/**
 * Remove-device confirmation (migrated from Retool
 * moduleBillableDevices/archiveModal — the API archives rather than deletes).
 */
function ArchiveBillableDeviceModal({
  device,
  onClose,
}: IArchiveBillableDeviceModalProps) {
  const { showToast } = useToast();
  const { archiveBillableDevice, isArchivingBillableDevice } =
    useArchiveBillableDevice();

  const handleRemove = async () => {
    try {
      await archiveBillableDevice(device.id);
      showToast("Device successfully removed from billable list", "success");
      onClose();
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error removing this device from billable list"
        ),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Remove device</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Are you sure you want to remove <strong>{device.deviceName}</strong>{" "}
          from the billable device list?
        </ModalBodyText>
        <ModalFormActions>
          <FilterButton variant="secondary" onClick={onClose}>
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isArchivingBillableDevice}
            onClick={handleRemove}
          >
            {isArchivingBillableDevice ? "Removing..." : "Remove"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}

/**
 * Billable devices admin page (EN-5129) — first page migrated from Retool
 * (page_BillableDevices + moduleBillableDevices). Lists the active billable
 * device catalog, adds a device type from the source-type catalog, and
 * archives entries — all through the same /Retool/BillableDevices endpoints
 * the Retool app uses.
 */
export function AdminBillableDevices() {
  document.title = "Billable devices - Admin";

  const { openModal, closeModal } = useModal();
  const { billableDevices, isLoading, isError } = useBillableDevices();

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({
    data: billableDevices,
    searchFields: ["deviceName", "manufacturer", "fdaNumber"],
  });

  const handleAddClick = () => {
    openModal(
      <AddBillableDeviceModal
        existingDevices={billableDevices}
        onClose={closeModal}
      />,
      { width: "440px", showCloseButton: true }
    );
  };

  const handleArchiveClick = (device: IBillableDevice) => {
    openModal(
      <ArchiveBillableDeviceModal device={device} onClose={closeModal} />,
      {
        width: "440px",
        showCloseButton: true,
      }
    );
  };

  const columns: ColumnDef<IBillableDevice, string>[] = useMemo(
    () => [
      { accessorKey: "id", header: "ID", enableSorting: true },
      { accessorKey: "deviceName", header: "Device name", enableSorting: true },
      {
        accessorKey: "created",
        header: "Created",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.created)}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "fdaNumber",
        header: "FDA number",
        cell: ({ row }) => <span>{row.original.fdaNumber || "-"}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "manufacturer",
        header: "Manufacturer",
        cell: ({ row }) => <span>{row.original.manufacturer || "-"}</span>,
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowAction
            label="Remove"
            icon={<TrashIcon />}
            danger
            onClick={() => {
              handleArchiveClick(row.original);
            }}
          />
        ),
      },
    ],
    [billableDevices]
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Billable devices</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by device name, manufacturer, or FDA number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FiltersActions>
          <FilterButton variant="primary" onClick={handleAddClick}>
            Add billable device
          </FilterButton>
        </FiltersActions>
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load billable devices. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-billable-devices"]]}>
          <TableSorted
            columns={columns}
            data={items}
            emptyStateText={
              isLoading ? <TableLoadingState /> : "No billable devices found"
            }
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "billable-devices",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PageContainer>
  );
}
